<template lang="pug">
section.wam-content.builder-cclone(
    v-if="hasUserAccessToCclone"
    :class="builderCcloneClass")

    //- Loading data
    template(v-if="isLoadingCclone.list")
        spinner.l-flex-row--center.l-align-center(width="200" height="200")

    template(v-else)
            //- zero state
            template(v-if="availableCclones.length === 0")
                section.l-flex-column.is-empty.zero-state
                    p.zero-state-message Oh no 😕
                        br
                        | There is absolutely no CClone available yet.

            //- cclones list
            template(v-else)
                Panel(panelClassName="builder-cclone-list")
                    template(slot="header")
                        sectionHeader(title="cclones")
                            template(slot="action")
                                <button class="btn-border btn--small btn--round l-flex-row l-align-center" @click="exportDataForm('cclone')">
                                    | CClone export
                                </button>

                    template(slot="content")
                        //- SEARCH
                        div.search-criteria-wrapper
                            input.search-criteria(
                                :class="{'isFocus':searchValue!==''}"
                                placeholder="Search cclone"
                                v-model="searchValue"
                                @keyup.esc="resetSearchValue()"
                                @keyup.enter="launchSearch()")
                            svg-icon.search-reset(name='close' size='small' @click.native="resetSearchValue()" v-if="searchValue!==''")
                            div.search-filter
                                | Filter on :
                                select.search-targets(v-model="searchTarget")
                                    option(v-for="item in searchTargets", :key="item.value" :value="item.value") {{ item.label }}

                        //- CCLONE LIST
                        div.cclone-list-wrapper.scrollbar.is-scrollable
                            div.no-results(v-if="showNoResultFound") No results found

                            ul.tree-view.cclone-list.scrollbar.is-scrollable
                                li.tree-item.cclone-item(
                                    v-for="cclone in availableCclones" :key="cclone.id"
                                    :class="{'is-selected': cclone.id === selectedCcloneId}")
                                    CCloneItem(:cclone="cclone")

                section.l-flex-column.builder-cclone-information.zero-state(v-if="selectedCcloneId===null")
                    div.zero-state-message.l-flex-row--center Select a CClone
                        |
                        br
                        | &nbsp;&nbsp;&nbsp;&nbsp;and create an audience

                section.l-flex-column.builder-cclone-information.is-scrollable.scrollbar(v-else)
                    section.cclone-graph
                        sectionHeader(:title="ccloneInfo.label")
                            template(slot="action")
                                <button class="btn-border btn--small btn--round l-flex-row l-align-center" :disabled="disabledCreateAudience" @click="createAudienceForm">
                                    | Save Audience
                                </button>

                        template(v-if="isLoadingCclone.chart")
                            spinner.l-flex-row--center.l-align-center(width="200" height="200")

                        template(v-else)
                            div.cclone-graph-main(v-if="!hasChartData")
                                div No data available for this CClone

                            div.cclone-graph-main(v-else)
                                div.l-flex-row.cclone-profile
                                    span.cclone-profile-label Profile
                                    div.switch-wrapper
                                        div.switch-content(:class="{'top': switchProfileState}")
                                            div.cclone-profile-switch(@click.prevent="switchProfile('all')")
                                                label(for="non-empty_profile") Non-Empty
                                                input(
                                                    type="radio"
                                                    name="switch-profile"
                                                    id="non-empty_profile"
                                                    value="filled_profiles"
                                                    checked
                                                )
                                            div.cclone-profile-switch(@click.prevent="switchProfile('nonEmpty')")
                                                label(for="all_profile") All
                                                input(
                                                    type="radio"
                                                    name="switch-profile"
                                                    id="all_profile"
                                                    value="all_users"
                                                )

                                //- GRAPH
                                CCloneChart(
                                    v-if="showCcloneChart"
                                    :ccloneData="chartData"
                                    :selectedProfile="selectedProfile"
                                )

                            section.cclone-decision-tree(v-if="showCcloneChart")
                                sectionHeader(title="Decision tree")
                                div(v-show="showDecisionTree")
                                    div(v-if="decisionTree.status !== 'success'") {{ errorMessage }}
                                    div.cclone-decision-tree-main(v-else)
                                        div.cclone-decision-tree-content
                                            img.cclone-decision-tree-image(:src="decisionTreeImagePath" @click="extendDecisionTree()")

                //- CCLONE DETAIL
                CCloneDetails(
                    v-if="selectedCcloneId !== null"
                    :ccloneInfo="ccloneInfo"
                )

                //- Modal
                div.overlay-close(
                    v-if="displayAudienceForm || displayDecisionTree"
                    @click="closeModal()")
                div.modal(
                    v-if="displayAudienceForm && !displayExportForm || displayDecisionTree"
                    :class="[{'modal-h-full modal-form-audience': displayAudienceForm}]")
                    FormAudience(
                        v-if="displayAudienceForm"
                        :mode="'cclone'")
                    main.modal-wrapper(v-if="displayDecisionTree")
                        svg-icon.close-modal.cursor(name='close' size="small" @click.native="closeModal()")
                        div.modal-content.cclone-decision-tree-image-full
                            img(:src="decisionTreeImagePath")
</template>

<script>
import { isEmpty, isNil, includes, pick, get, keysIn, find } from 'lodash'

//avoid repeating namespace for each store action/mutation/getter/state
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'

//components
import CCloneDetails from '@/components/builder/CCloneDetails'
import CCloneChart from '@/components/builder/CCloneChart'
import CCloneItem from '@/components/builder/CCloneItem'
import FormAudience from '@/components/builder/FormAudience'
import Panel from '@/components/common/Panel'
import SectionHeader from '@/components/common/SectionHeader'

export default {
    name: 'BuilderCclone',

    props: {
        disabledCreateAudience: {
            default: false,
            type: Boolean,
        },
    },

    components: {
        CCloneChart,
        CCloneItem,
        FormAudience,
        Panel,
        SectionHeader,
        CCloneDetails,
    },

    data() {
        return {
            selectedProfile: 'nonEmpty',
            switchProfileState: false,
            searchValue: '',
            searchResultsList: {},
            searchTargets: [
                { label: 'Label', value: 'label' },
                { label: 'Id', value: 'id' }
            ],
            searchTarget: 'label',
            mode: 'selection',
            displayDecisionTree: false
        }
    },

    created() {
        if (this.hasUserAccessToCclone) {
            this.retrieveItems()
        }
    },

    watch: {
        hasUserAccessToCclone: {
            handler: function(val) {
                if (val) {
                    this.retrieveItems()
                }
            }
        },
        searchValue: {
            handler: function(newValue) {
                if (newValue === '') this.mode = 'selection'
            }
        },
        searchTarget: {
            handler: function(newValue, oldValue) {
                if (this.searchValue !== '' && newValue !== oldValue) {
                    this.launchSearch()
                }
            }
        }
    },

    computed: {
        // Call to state
        ...mapState('ui/builder', {
            displayForm: state => state.formAudience.display,
            audienceFormMode: state => state.formAudience.mode,
            isLoadingCclone: state => state.isLoading.cclone
        }),
        ...mapState('ui/cclone', {
            selectedCcloneId: state => state.selectedCcloneId
        }),
        ...mapState('exports', {
            displayExportForm: state => state.formExport.display
        }),
        ...mapState('account', {
            accountId: state => state.accountId
        }),
        ...mapState('data/cclone', {
            ccloneDetails: state => state.ccloneDetails,
            decisionTree: state => state.decisionTree
        }),
        // Call to getters
        ...mapGetters('data/cclone', ['getAvailableCclones', 'getItemById']),
        ...mapGetters('account', ['getAccessToCclone', 'getIsAudienceCreationAllowed']),
        ...mapGetters('ui/builder', ['getSearchResults']),

        // UI
        hasUserAccessToCclone() {
            return this.getAccessToCclone()
        },
        builderCcloneClass() {
            if ((this.isLoadingCclone.list || this.availableCclones.length === 0) && this.selectedCcloneId === null) return 'full-width'
            if (this.selectedCcloneId === null) return 'not-selected'
            return false
        },

        // Search results WITH data
        showNoResultFound() {
            return this.mode === 'search' && isEmpty(this.searchResultsList)
        },

        // cclones list
        availableCclones() {
            return this.mode === 'search' ? this.searchResultsList : this.getAvailableCclones()
        },
        hasDetails() {
            const ccloneDetailsIds = keysIn(this.ccloneDetails)
            return includes(ccloneDetailsIds, this.selectedCcloneId)
        },

        // cclone detail
        ccloneInfo() {
            const _info = this.getItemById({ id: this.selectedCcloneId })
            // @TODO error notification
            if (_info === false) return null
            return _info
        },

        // chart
        showCcloneChart() {
            return !isNil(this.selectedCcloneId) && !isNil(find(this.ccloneDetails, (cc, key) => key === this.selectedCcloneId)) && !isEmpty(this.chartData)
        },
        selectedPoint() {
            if (this.ccloneDetails[this.selectedCcloneId]) return this.ccloneDetails[this.selectedCcloneId].selectedPoint
            return []
        },
        chartData() {
            const currentChartData = pick(this.ccloneDetails, this.selectedCcloneId)
            return get(currentChartData, `${this.selectedCcloneId}.chartData.${this.selectedProfile}`, [])
        },
        hasChartData() {
            return !isEmpty(this.chartData)
        },

        //decision tree
        showDecisionTree() {
            return !isEmpty(this.selectedPoint) && (!isNil(this.decisionTree.status) || !isNil(this.decisionTree.image))
        },
        errorMessage() {
            return this.decisionTree.status
        },
        decisionTreeImagePath() {
            return 'data:image/jpeg;base64,' + this.decisionTree.image
        },

        // forms
        displayAudienceForm() {
            return this.displayForm && this.audienceFormMode === 'cclone'
        }
    },

    methods: {
        // Call to mutations
        ...mapMutations('exports', ['setExportForm']),
        ...mapActions('data/cclone', ['retrieveItems']),
        ...mapActions('ui/builder', ['closeAudienceForm']),
        ...mapMutations('ui/builder', ['setDisplayFormAudience']),

        exportDataForm(type) {
            this.setExportForm({
                display: true,
                type
            })
        },

        closeModal() {
            this.displayDecisionTree = false
            this.closeAudienceForm()
        },

        switchProfile(profile) {
            this.switchProfileState = !this.switchProfileState
            this.selectedProfile = profile
        },

        extendDecisionTree() {
            this.displayDecisionTree = true
        },

        launchSearch() {
            this.mode = 'search'
            this.searchResultsList = get(
                this.getSearchResults({
                    searchPattern: this.searchValue.trim(),
                    category: ['cclone'],
                    type: this.searchTarget
                }),
                'cclone',
                {}
            )
        },
        resetSearchValue() {
            this.mode = 'selection'
            this.searchValue = ''
        },

        createAudienceForm() {
            if (!this.disabledCreateAudience) {
                this.setDisplayFormAudience({
                    display: true,
                    mode: 'cclone'
                })
            }
        }
    }
}
</script>
